// A claimed device's CDS record on its own page (#2118, replacing #2038's onboarding card): a small
// chip at the end of the line under the device's name, drawn only when the CDS reading needs
// attention, and the same record behind a separated *CDS record…* item in the head's Actions menu.
// Both open one popover: where CDS points the device, who holds the record, this service's transfer
// request, when CDS was read, and **Read CDS again**. #2038's checklist of what CDS cannot see, for a
// device the cell has never heard from, is not drawn (Gabriel Provencher, 2026-10-29, on #2118: *"2. no"*).
//
// **The chip and the menu item are a superuser's alone** (Gabriel Provencher, 2026-10-29, on #2118:
// the card *"take[s] WAY too much space"*, and the look was approved as a superuser's). That narrows
// what the page shows and not what the API answers: ADR-0494 decision 4 still serves `held_by_shasta`
// to every caller who may read the device, and the dated note under that decision says so. The
// caller decides who sees this (`Devices.tsx`); nothing here reads a role.
//
// **Everything here is the API's.** The CDS reading, the transfer request, why a held request is
// held, and whether the cell has heard from the device are `GET /v1/devices/{serial}`'s, and this page
// decides none of them. The acts are `POST /v1/devices/{serial}/cds-record:read`, an operator's click,
// and **one act per request state, a superuser's** (ADR-0522 decision 5, #2212): *Ask now* for a device
// with no request or a refused or failed one (`POST /v1/sites/{id}/cutover:request` naming it alone,
// the bulk ask down to one, which is #2199's act); *Approve* for a record another organization holds
// (`cds-transfer:approve`); *Release* for a held request whose device's own route is refused
// (`cds-transfer:release`, ADR-0514, #2176; ADR-0516, #2187), behind a confirmation; and none while a
// request is in flight or transferred (`cdsActionOf`). Nothing here reads CDS on a timer or on a page
// load.
//
// **No lateness verdict.** Whether a device "should have arrived by now" is a §29 value nobody has
// measured, so the page says how long ago CDS was read and nothing about whether that is too long.
import { useEffect, useId, useRef, useState, type RefObject } from "react";
import { api, idempotencyKey } from "../../api/client";
import { useMutation } from "../../api/mutation";
import type { components } from "../../api/schema";
import { AGE_TICK_MS, ageWords, useNow } from "../../shell/freshness";
import { Popover, ProblemBox } from "../../ui";
import { unrecognized } from "../../api/openEnum";

type Device = components["schemas"]["Device"];
type CdsState = components["schemas"]["CdsRecordState"];
type Transfer = NonNullable<Device["cds_transfer"]>;

/** The transfer states the chip says *transfer requested* for: held for the site's first apply, sent
 * and unanswered, or accepted. Refused, failed and transferred leave the chip to the CDS reading, and
 * the popover says what happened to the request. */
const REQUESTED: ReadonlySet<string> = new Set(["held_for_apply", "pending", "accepted"]);

export interface CdsChip { label: string; tone: "warn" | "info" | "none" }

/** The chip for a device's CDS reading, or none where nothing needs attention. **Never for
 * `points_here`**, whatever the transfer says. Amber and blue are drawn on any device, because each
 * means a transfer is due or in flight. **A grey state is drawn only for a device the cell has never
 * heard from** (`onboarding.arrival: not_heard_from`): once it has arrived, a CDS reading
 * that places nothing needs no attention (Gabriel Provencher, 2026-10-29, on #2118: *"1. build it"*).
 * Actions › *CDS record…* still reaches the reading on every device. A state this page does not know
 * is grey and never read as pointing here. */
export function cdsChipOf(read: Pick<Device, "cds_record" | "cds_transfer" | "onboarding">): CdsChip | undefined {
  const cds = read.cds_record;
  const t = read.cds_transfer;
  if (cds?.state === "points_here") return undefined;
  if (t && REQUESTED.has(t.state)) return { label: "CDS · transfer requested", tone: "info" };
  const grey = (label: string): CdsChip | undefined =>
    read.onboarding?.arrival === "not_heard_from" ? { label, tone: "none" } : undefined;
  switch (cds?.state) {
    case "points_elsewhere": return { label: `CDS · ${cds.endpoint ?? "elsewhere"}`, tone: "warn" };
    // Restates ADR-0494 decision 4 and its dated note of 2026-10-29 (#2118) — Shasta is the one holder
    // ever named, and on this page it is named to a superuser alone, though the API serves it to all.
    case "held_by_shasta": return { label: "CDS · Shasta", tone: "warn" };
    case "not_readable": return { label: t?.state === "awaiting_approval" ? "CDS · other org · approve" : "CDS · other org", tone: "warn" };
    case "no_record": return grey("CDS · no record");
    case "unknown": return grey("CDS · not answered");
    case undefined: return grey("CDS · not read");
    default: return grey("CDS · state not known");
  }
}

/** The site's device list's CDS chip (ADR-0522 decision 6, #2212), without the device page's `CDS · `,
 * which the column's head says: **empty for `points_here` and for a request answered `transferred`**,
 * whatever else the row says, so a healthy site's column is empty, and for a row with neither a reading
 * nor a request. Every other row is drawn, a grey state included: the list composes no arrival, so a
 * reading that places nothing is said as it is. A filter on "CDS not pointing here" is this chip's being
 * drawn, so what the filter counts is what the column shows. */
export function cdsListChipOf(d: Pick<Device, "cds_record" | "cds_transfer">): CdsChip | undefined {
  const cds = d.cds_record;
  const t = d.cds_transfer;
  if (!cds && !t) return undefined;
  if (cds?.state === "points_here" || t?.state === "transferred") return undefined;
  if (t && REQUESTED.has(t.state)) return { label: "transfer requested", tone: "info" };
  switch (cds?.state) {
    case "points_elsewhere": return { label: cds.endpoint ?? "elsewhere", tone: "warn" };
    case "held_by_shasta": return { label: "Shasta", tone: "warn" };
    case "not_readable": return { label: t?.state === "awaiting_approval" ? "other org · approve" : "other org", tone: "warn" };
    case "no_record": return { label: "no record", tone: "none" };
    case "unknown": return { label: "not answered", tone: "none" };
    case undefined: return { label: "not read", tone: "none" };
    default: return { label: "state not known", tone: "none" };
  }
}

/** The claim's one line about CDS (ADR-0522 decision 6, item 5; #2213): what the claim's own answer
 * says CDS holds, then what became of the request it decided, as `CDS: held by Shasta — transfer
 * requested`. **Shown to anyone who can claim** (decision 6, D5), so it says only what the claim's answer
 * carries for that caller: `not_readable` and `awaiting_approval` reach a superuser alone, and any other
 * caller's answer already reads `unknown` and no request in their place (ADR-0457 decision 1). A claim
 * with no reading says so, which is every claim where this deployment holds no CDS credential. */
export function cdsClaimLine(d: Pick<Device, "cds_record" | "cds_transfer">): string {
  const cds = d.cds_record;
  const t = d.cds_transfer;
  const held = (() => {
    switch (cds?.state) {
      case "points_here": return "already points here";
      case "points_elsewhere": return cds.endpoint ? `points to ${cds.endpoint}` : "points elsewhere";
      // Restates ADR-0494 decision 4 — Shasta is the one holder ever named.
      case "held_by_shasta": return "held by Shasta";
      case "not_readable": return "another organization holds its record";
      // Restates the ruling on #2115 — CDS holding no record means nothing is asked of the placement service.
      case "no_record": return "holds no record of it";
      case "unknown": return "did not answer";
      case undefined: return "not read";
      default: return "answered in a way this page does not know";
    }
  })();
  const asked = (() => {
    switch (t?.state) {
      case undefined: return "";
      // Every request state reads as the transfer's, so the line's second half always names its subject.
      case "awaiting_approval": return "transfer waits for a superuser's approval";
      case "held_for_apply": case "pending": case "accepted": return "transfer requested";
      case "transferred": return "transferred";
      case "refused": return "transfer refused";
      case "failed": return "transfer failed";
      default: return "request in a state this page does not know";
    }
  })();
  return `CDS: ${held}${asked ? ` — ${asked}` : ""}.`;
}

/** Where CDS points the device, in words. A state this page does not know reads as not known, and
 * never as pointing here. Who holds an unreadable record is the *Held by* row's (`holderWords`), so
 * this row does not say it again. `points_here` names the company, *arpIQ*, which `web/COPY.md`, *The
 * company's name*, keeps off a page a customer can reach even where only a superuser is shown the
 * string: it is a named exception in `web/COPY.md`, ruled by Gabriel Provencher on 2026-10-31 on
 * #2235 (!2020): *"arpIQ is fine for the CDS"*. */
export function cdsWords(state: CdsState | string | undefined, endpoint?: string): string {
  switch (state) {
    case "points_here": return `arpIQ${endpoint ? ` (${endpoint})` : ""}`;
    case "points_elsewhere": return `Another controller${endpoint ? ` (${endpoint})` : ""}`;
    case "held_by_shasta": case "not_readable": return "Not readable";
    // Restates the ruling on #2115 (2026-10-29, narrowing ADR-0483 decision 4) — CDS holding no record
    // means CDS will not redirect the device, and a claim asks the placement service for nothing.
    case "no_record": return "Nothing. CDS won't redirect it.";
    case "unknown": return "Not known: CDS said nothing that places it";
    case undefined: return "Not known";
    default: return "Not known: CDS's answer is one this page does not know";
  }
}

/** Who holds the record, where the reading says something about it, or none. Restates ADR-0494, *What
 * the build chose* — a record CDS read (`points_here`, `points_elsewhere`) is one this service's key
 * can read, which is its own organization's or nobody's, and the API cannot tell the two apart, so no
 * row is drawn for it rather than one saying nothing; another organization is never named. */
export function holderWords(state: CdsState | string | undefined): string | undefined {
  switch (state) {
    case "held_by_shasta": return "Shasta";
    case "not_readable": return "Another organization";
    case "no_record": return "Nobody: there is no record";
    default: return undefined;
  }
}

/** What this controller's request to the placement service says, in words, with the time of the event
 * the state names: `answered_at` for an answer, `sent_at` for a request sent and not answered. Where
 * that time is absent no time is said, and `requested_at`, when the request was decided, is never said
 * in its place. A state this page does not know is said to be one, and never read as done. */
export function transferWords(
  t: Pick<Transfer, "state" | "reason" | "held_reason" | "sent_at" | "answered_at">, now: number,
): string {
  const at = (iso: string | undefined) => (iso ? ` ${ago(iso, now)}` : "");
  switch (t.state) {
    case "awaiting_approval": return "Waiting for a superuser's approval: another organization holds the record.";
    // Restates ADR-0522 decision 2 as ADR-0524 amends it, and ADR-0516 decision 2 (#2212; ADR-0522 supersedes in
    // part ADR-0483 decision 9 and ADR-0516 decision 1's switch term) — a request is held only for a device not
    // connected to its controller at a site with nothing applied, whose seed's record the cell does not hold, and the site's first applied document of any kind sends it.
    // Why it is held is read per device: an access point at a site the venue path refuses has no apply until
    // it is reconciled; a switch's own first configuration is one such document, which the cell can take only
    // where it holds a stub for it on a model and build open for one.
    case "held_for_apply": return heldWords(t.held_reason);
    // Absent `sent_at` is a request decided and not sent, which "Sent" would make false.
    case "pending": return t.sent_at ? `Sent${at(t.sent_at)}, no answer yet` : "Not sent yet";
    case "accepted": return `Accepted${at(t.answered_at)}`;
    // Restates ZENITH.md §11.2 and ADR-0457 decision 3 — the record moved is not the device arrived:
    // it follows only when it next looks CDS up.
    case "transferred": return `Moved${at(t.answered_at)}. The device follows the next time it checks CDS.`;
    case "refused": return `Refused${t.reason ? ` (${t.reason})` : ""}`;
    case "failed": return "Failed. Read CDS again to see where it points.";
    default: return "In a state this page does not know";
  }
}

function heldWords(reason: Transfer["held_reason"]): string {
  switch (reason) {
    case "site_cannot_apply": return "Waiting for the site's first apply. The site can't apply until it is reconciled.";
    case "switch_cannot_apply": return "Waiting for the site's first apply. No configuration can be applied to this switch yet.";
    case "switch_first_document_open": return "Waiting for the site's first apply. This switch's first configuration counts as one.";
    case "no_applied_document": case undefined: return "Waiting for the site's first apply";
    // Restates ADR-0511 decision 5a — a reason this build does not know is shown as received, and
    // is not read as one it does.
    default: return `Waiting for the site's first apply, for a reason this page does not recognize (${unrecognized(reason)})`;
  }
}

/** The popover's one act for the request's state (ADR-0522 decision 5, #2212), or none:
 * - `ask`: no request, or one refused or failed, for a record that does not point here. Asking reads
 *   CDS again and decides a new request, as the site's device list's bulk ask does for a set.
 * - `approve`: a request awaiting a superuser's approval, for a record another organization holds.
 * - `release`: a request held for the site's first apply whose device's own route the read says is
 *   refused, which is where the release is admitted. A held request whose route is open has none: its
 *   site's first applied document sends it, and so does asking once the device is connected.
 * - none: a request in flight (`pending`, `accepted`), `transferred`, or a state this page does not
 *   know, and a record that points here. */
export type CdsAction = "ask" | "approve" | "release";

export function cdsActionOf(read: Pick<Device, "cds_record" | "cds_transfer">): CdsAction | undefined {
  const t = read.cds_transfer;
  if (read.cds_record?.state === "points_here") return undefined;
  switch (t?.state) {
    case undefined: case "refused": case "failed": return "ask";
    case "awaiting_approval": return "approve";
    case "held_for_apply":
      return t.held_reason === "site_cannot_apply" || t.held_reason === "switch_cannot_apply" ? "release" : undefined;
    default: return undefined;
  }
}

/** "3 min ago", from an ISO stamp, against `now`. */
function ago(iso: string, now: number): string {
  return `${ageWords(now - new Date(iso).getTime())} ago`;
}

/** The record's rows and its one act: the popover's content, the same from the chip and from the
 * Actions menu. */
export function CdsDetails({ read, onReread }: { read: Device; onReread(): void }) {
  const now = useNow(AGE_TICK_MS);
  const m = useMutation();
  const rel = useMutation();
  const act = useMutation();
  const [armed, setArmed] = useState(false);
  const [asked, setAsked] = useState(false);
  const cds = read.cds_record;
  const t = read.cds_transfer;
  const holder = holderWords(cds?.state);
  const reread = async () => {
    const ok = await m.run(() => api.POST("/devices/{serial}/cds-record:read", {
      params: { path: { serial: read.serial }, header: { "Idempotency-Key": idempotencyKey() } },
    }));
    if (ok) onReread();
  };
  // One act per state, a superuser's (cdsActionOf). The popover is a superuser's alone
  // (`DeviceHead.tsx`), and the API answers 404 to anyone else regardless.
  const action = cdsActionOf(read);
  const releasable = action === "release";
  const isSwitch = t?.held_reason === "switch_cannot_apply";
  // Ask now: the bulk ask naming this device alone. It answers before CDS is read again, so the
  // request is read on the device afterwards and nothing here says what was decided.
  const askNow = async () => {
    if (!read.site_id) return;
    const siteId = read.site_id;
    setAsked(false);
    const ok = await act.run(() => api.POST("/sites/{id}/cutover:request", {
      params: { path: { id: siteId }, header: { "Idempotency-Key": idempotencyKey() } },
      body: { serials: [read.serial] },
    }));
    if (ok) { setAsked(true); onReread(); }
  };
  const approve = async () => {
    const ok = await act.run(() => api.POST("/devices/{serial}/cds-transfer:approve", {
      params: { path: { serial: read.serial }, header: { "Idempotency-Key": idempotencyKey() } },
    }));
    if (ok) onReread();
  };
  const release = async () => {
    const ok = await rel.run(() => api.POST("/devices/{serial}/cds-transfer:release", {
      params: { path: { serial: read.serial }, header: { "Idempotency-Key": idempotencyKey() } },
    }));
    if (ok) { setArmed(false); onReread(); }
  };
  return (
    <div className="cds-record">
      <dl className="cds-rows">
        <dt>Points at</dt><dd>{cdsWords(cds?.state, cds?.endpoint)}</dd>
        {holder && <><dt>Held by</dt><dd>{holder}</dd></>}
        {/* Restates ADR-0483 decision 3, ADR-0522 decision 2 as ADR-0524 amends it, ADR-0514 as ADR-0516 amends it, and ZENITH.md §11.2
            (ADR-0522 superseding ADR-0483 decision 9 in part, #2212) — the request names the site and never a cell,
            the placement service moves the record, a request is held only for a device not connected to its
            controller at a site with nothing applied, whose seed's record the cell does not hold, until the site's first applied document sends it or,
            where the device's own route is refused, a superuser releases it, and a transfer is not arrival. */}
        <dt>Transfer</dt><dd>{t ? transferWords(t, now) : "None requested"}</dd>
        {/* The API carries when CDS was read and not on which occasion (claim, import or an operator's ask). */}
        <dt>Read</dt><dd>{cds ? ago(cds.read_at, now) : "Not read here"}</dd>
      </dl>
      {/* Restates ADR-0457 decision 3 and ADR-0494 decision 7, and #2107 — CDS pointing here is not arrival: CDS is
          one of the places a device looks for its controller, and not the first. CDS is read when the device is
          claimed, when someone asks, and once when the placement service answers the transfer, and never on a timer.
          Reading it again also sends an unanswered (`pending`) request again and asks the placement service for an
          accepted one's answer; only the first is said under the button, because only it is a send (#2235). */}
      <p className="muted small">CDS pointing here doesn't mean the device has arrived.</p>
      <button type="button" className="btn" disabled={m.busy} onClick={reread}>
        {m.busy ? "Reading CDS…" : "Read CDS again"}
      </button>
      {t?.state === "pending" && <p className="muted small">Also resends the transfer request.</p>}
      {m.problem && <ProblemBox problem={m.problem} />}
      {/* Restates ADR-0522 decisions 1, 4 and 5 (#2212) — asking reads CDS again and decides a new request,
          sent at once unless the interim hold keeps it, and a record another organization holds is moved
          only on a superuser's approval. */}
      {action === "ask" && read.site_id && (
        <button type="button" className="btn" disabled={act.busy} onClick={() => void askNow()}>
          {act.busy ? "Asking…" : "Ask now"}
        </button>
      )}
      {action === "approve" && (
        <button type="button" className="btn" disabled={act.busy} onClick={() => void approve()}>
          {act.busy ? "Approving…" : "Approve the transfer"}
        </button>
      )}
      {asked && <p className="muted small" role="status">Asked. CDS is read again and the request decided; it shows here once you read the device again.</p>}
      {act.problem && <ProblemBox problem={act.problem} />}
      {releasable && !armed && (
        <button type="button" className="btn btn-quiet" onClick={() => setArmed(true)}>Release the transfer request…</button>
      )}
      {releasable && armed && (
        <div className="confirm" role="group" aria-label="Release the transfer request">
          {/* Restates ADR-0516 decision 3, amending ADR-0514 decision 3, on Gabriel Provencher's ruling of
              2026-10-30 — released before any apply, an access point most likely runs the cell's built-in
              OpenWifi default, whose password is published, and a switch keeps what it runs and is sent nothing. */}
          {isSwitch ? (
            <p className="small">
              Released now, the switch keeps the configuration it is running, and Zenith sends it nothing until its
              own first configuration or port plan.
            </p>
          ) : (
            <p className="small">
              Released now, the access point will most likely come up on the cell's built-in default, which
              broadcasts a network named OpenWifi that anyone can join because its password is published, until
              Zenith sends it a configuration. Reconciling the site instead sends this request with a configuration
              Zenith sent, and avoids that.
            </p>
          )}
          <button type="button" className="btn btn-danger" disabled={rel.busy} onClick={release}>
            {rel.busy ? "Releasing…" : "Release it"}
          </button>
          <button type="button" className="btn btn-quiet" disabled={rel.busy} onClick={() => setArmed(false)}>Keep it held</button>
        </div>
      )}
      {rel.problem && <ProblemBox problem={rel.problem} />}
    </div>
  );
}

/** The record's popover, anchored at whatever opened it. Focus moves into it when it opens, so the
 * keyboard reaches its button, and Escape gives it back to the anchor (`Popover`). */
export function CdsPopover({ id, open, anchor, onClose, read, onReread }: {
  id?: string; open: boolean; anchor: RefObject<HTMLElement | null>; onClose(): void; read: Device; onReread(): void;
}) {
  const body = useRef<HTMLDivElement>(null);
  useEffect(() => { if (open) body.current?.focus(); }, [open]);
  return (
    <Popover id={id} open={open} anchor={anchor} onClose={onClose} label={`CDS record for ${read.name}`}>
      <div ref={body} tabIndex={-1} className="cds-popover">
        <CdsDetails read={read} onReread={onReread} />
      </div>
    </Popover>
  );
}

/** The chip at the end of the line under the name, and its popover. Nothing is drawn where the
 * reading needs no attention. */
export function CdsChipButton({ read, onReread }: { read: Device; onReread(): void }) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const id = useId();
  const chip = cdsChipOf(read);
  if (!chip) return null;
  return (
    <>
      <button ref={anchor} type="button" className={`pill pill-${chip.tone} cds-chip`}
        aria-label={`${chip.label}. Show the CDS record`} aria-expanded={open} aria-controls={open ? id : undefined}
        onClick={() => setOpen((o) => !o)}>
        <span className="dot" aria-hidden="true" />{chip.label}
      </button>
      <CdsPopover id={id} open={open} anchor={anchor} onClose={() => setOpen(false)} read={read} onReread={onReread} />
    </>
  );
}
